import { jsPDF } from 'jspdf';
import { formatCurrencyInFloat } from './formatters';
import { printTimeToMinutes } from './priceCalculator';

const PAGE_MARGIN = 14;
const FOOTER_HEIGHT = 10;
const LINE_HEIGHT = 6;
const SECTION_GAP = 8;

function formatMinutes(totalMinutes) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${String(minutes).padStart(2, '0')}min`;
}

function formatPercent(value) {
    return `${Number(value || 0).toFixed(1).replace('.', ',')}%`;
}

function formatInputMoney(value) {
    const n = parseFloat(value);
    if (!Number.isFinite(n)) {
        return '—';
    }
    return formatCurrencyInFloat(n);
}

function formatInputPercent(value) {
    const n = parseFloat(value);
    if (!Number.isFinite(n)) {
        return '—';
    }
    return `${n.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
}

function formatInputNumber(value, suffix = '') {
    const n = parseFloat(value);
    if (!Number.isFinite(n)) {
        return '—';
    }
    return `${n.toLocaleString('pt-BR')}${suffix}`;
}

function slugifyProductName(name) {
    return String(name || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .replace(/-{2,}/g, '-');
}

function getCalculatorPublicUrl() {
    if (typeof window !== 'undefined' && window.location?.origin) {
        return `${window.location.origin.replace(/\/+$/, '')}/calculator`;
    }

    const apiBase = (process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/+$/, '');
    if (apiBase) {
        try {
            const normalized = apiBase.includes('://') ? apiBase : `https://${apiBase}`;
            const url = new URL(normalized);
            return `${url.origin}/calculator`;
        } catch {
            /* fall through */
        }
    }

    return '/calculator';
}

function getStoredUser() {
    try {
        const raw = localStorage.getItem('user');
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function buildPdfTitle(productName) {
    const trimmed = String(productName || '').trim();
    if (trimmed) {
        return `Simulação de preço para ${trimmed}`;
    }
    return 'Simulação de preço 3D';
}

function buildPdfFileName(productName) {
    const dateStamp = new Date().toISOString().slice(0, 10);
    const slug = slugifyProductName(productName);
    if (slug) {
        return `${slug}-${dateStamp}.pdf`;
    }
    return `calculadora-preco-${dateStamp}.pdf`;
}

function unitSubtitle(amount, quantity, exactUnit) {
    const qty = parseInt(quantity, 10);
    if (!Number.isFinite(qty) || qty <= 1) {
        return '';
    }
    const unit = exactUnit != null ? exactUnit : amount / qty;
    return `${formatCurrencyInFloat(unit)} por unidade`;
}

function filamentPdfLabel(line) {
    if (!line?.name) {
        return 'Filamento';
    }
    if (String(line.name).startsWith('Filamento')) {
        return line.name;
    }
    return `Filamento · ${line.name}`;
}

function filamentPdfNote(line) {
    if (!line || !(line.grams > 0)) {
        return '';
    }
    const grams = Number(line.grams).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
    return `${grams} g × ${formatCurrencyInFloat(line.costPerKilogram)}/kg`;
}

function buildProductionRows(quote) {
    const quantity = quote.quantity;
    const rows = [
        { label: 'Energia (luz)', amount: quote.energy },
        ...(quote.filamentLines || [])
            .filter((line) => line.cost > 0)
            .map((line) => ({
                label: filamentPdfLabel(line),
                amount: line.cost,
                note: filamentPdfNote(line),
            })),
        { label: 'Depreciação da impressora', amount: quote.depreciation },
        {
            label: 'Embalagem',
            amount: quote.packaging,
            unitCaption: unitSubtitle(quote.packaging, quantity, quote.packagingUnit),
        },
        {
            label: 'Outros custos',
            amount: quote.other,
        },
        { label: 'Taxa de falhas', amount: quote.reworkReserve },
    ].filter((row) => row.amount > 0);

    if (quote.marketplaceFeeOnList > 0) {
        rows.push({
            label: 'Taxa de marketplace',
            amount: quote.marketplaceFeeOnList,
            unitCaption: unitSubtitle(quote.marketplaceFeeOnList, quantity, quote.marketplaceFeeUnit),
        });
    }
    if (quote.cardFeeOnList > 0) {
        rows.push({
            label: 'Taxa da maquininha',
            amount: quote.cardFeeOnList,
            unitCaption: unitSubtitle(quote.cardFeeOnList, quantity, quote.cardFeeUnit),
        });
    }
    if (quote.pjTaxFeeOnList > 0) {
        rows.push({
            label: 'Imposto PJ',
            amount: quote.pjTaxFeeOnList,
            unitCaption: unitSubtitle(quote.pjTaxFeeOnList, quantity, quote.pjTaxFeeUnit),
        });
    }

    const hasFees =
        quote.marketplaceFeeOnList > 0 || quote.cardFeeOnList > 0 || quote.pjTaxFeeOnList > 0;
    const total = hasFees
        ? quote.productionCost + quote.marketplaceFeeOnList + quote.cardFeeOnList + quote.pjTaxFeeOnList
        : quote.productionCost;
    rows.push({
        label: hasFees ? 'Total de custos' : 'Total de produção',
        amount: total,
        unitCaption: unitSubtitle(total, quantity),
    });

    return rows.map((row) => ({
        ...row,
        unitCaption: row.label === 'Taxa de falhas'
            ? ''
            : (row.unitCaption || unitSubtitle(row.amount, quantity)),
    }));
}

function buildInputRows(inputs) {
    const rows = [];
    const productName = String(inputs.productName || '').trim();
    if (productName) {
        rows.push(['Produto', productName]);
    }
    const quantity = parseInt(inputs.quantity, 10);
    if (Number.isFinite(quantity) && quantity > 0) {
        rows.push(['Quantidade', String(quantity)]);
    }

    if (inputs.multipartEnabled && Array.isArray(inputs.parts) && inputs.parts.length > 0) {
        rows.push(['Impressão multiparte', 'Sim']);
        inputs.parts.forEach((part, index) => {
            const label = part.name?.trim() ? part.name.trim() : `Peça ${index + 1}`;
            rows.push([
                label,
                `${part.time || '00:00'} · ${formatInputNumber(part.grams, ' g')} · ${formatInputMoney(part.costPerKilogram)}/kg`,
            ]);
        });
    } else {
        rows.push(['Tempo de impressão', inputs.printTime || '00:00']);
        rows.push(['Consumo do filamento', `${formatInputNumber(inputs.usedMaterial, ' g')}`]);
        rows.push(['Custo por kg', formatInputMoney(inputs.costPerKilogram)]);
    }

    rows.push(
        ['Consumo da impressora', `${formatInputNumber(inputs.consumption)} W`],
        ['Custo do kWh', formatInputMoney(inputs.energyCost)],
        ['Valor da impressora', formatInputMoney(inputs.printerValue)],
        ['Vida útil estimada', `${formatInputNumber(inputs.printerUsefulLifeHours)} h`],
    );

    rows.push(
        ['Embalagem por unidade', formatInputMoney(inputs.packagingCost)],
        ['Outros (montagem, pintura, etc.)', formatInputMoney(inputs.otherCosts)],
        ['Taxa de falhas', formatInputPercent(inputs.reworkPercent)],
        ['Taxa marketplace', formatInputPercent(inputs.marketplacePercent)],
        ['Taxa maquininha', formatInputPercent(inputs.cardFeePercent)],
        ['Imposto PJ', formatInputPercent(inputs.pjTaxPercent)],
        ['Markup sobre o custo', formatInputPercent(inputs.markup)],
        ['Valor desejado de venda', formatInputMoney(inputs.salePrice)],
    );

    return rows;
}

function downloadPdfBlob(doc, fileName) {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
    );

    try {
        const pdfBlob = doc.output('blob');
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.style.display = 'none';
        if (isMobile) {
            link.setAttribute('target', '_blank');
        }
        document.body.appendChild(link);
        setTimeout(() => {
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        }, isMobile ? 100 : 0);
    } catch {
        doc.save(fileName);
    }
}

function contentBottom(doc) {
    return doc.internal.pageSize.getHeight() - PAGE_MARGIN - FOOTER_HEIGHT;
}

function ensureSpace(doc, y, needed = LINE_HEIGHT) {
    if (y + needed > contentBottom(doc)) {
        doc.addPage();
        return PAGE_MARGIN;
    }
    return y;
}

function writeSectionTitle(doc, y, title) {
    y = ensureSpace(doc, y, LINE_HEIGHT + 2);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(30, 30, 30);
    doc.text(title, PAGE_MARGIN, y);
    return y + LINE_HEIGHT + 2;
}

function writeKeyValue(doc, y, label, value, options = {}) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const valueX = pageWidth - PAGE_MARGIN;
    const labelWidth = pageWidth - PAGE_MARGIN * 2 - 50;

    y = ensureSpace(doc, y, LINE_HEIGHT + (options.extraLine ? LINE_HEIGHT : 0));

    doc.setFont('helvetica', options.bold ? 'bold' : 'normal');
    doc.setFontSize(options.bold ? 11 : 10);
    doc.setTextColor(options.bold ? 30 : 80, options.bold ? 30 : 80, options.bold ? 30 : 80);

    const labelLines = doc.splitTextToSize(String(label), labelWidth);
    doc.text(labelLines, PAGE_MARGIN, y);

    doc.setTextColor(30, 30, 30);
    doc.text(String(value), valueX, y, { align: 'right' });

    let nextY = y + Math.max(LINE_HEIGHT, (labelLines.length - 1) * LINE_HEIGHT);

    if (options.subtitle) {
        nextY = ensureSpace(doc, nextY, LINE_HEIGHT);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text(String(options.subtitle), valueX, nextY, { align: 'right' });
        nextY += LINE_HEIGHT;
    }

    return nextY + 2;
}

function addFooters(doc, calculatorUrl) {
    const pageCount = doc.internal.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const footerText = `Simulação gerada por Acompanha 3D (${calculatorUrl})`;

    for (let page = 1; page <= pageCount; page += 1) {
        doc.setPage(page);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text(footerText, pageWidth / 2, pageHeight - PAGE_MARGIN, { align: 'center' });
    }
}

/**
 * Gera e baixa um PDF com entradas e resultado da calculadora de preço.
 * @param {{ inputs: object, quote: object, variant?: 'admin' | 'public' }} params
 */
export function downloadCalculatorPdf({ inputs, quote, variant = 'admin' }) {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const calculatorUrl = getCalculatorPublicUrl();
    const user = variant === 'admin' ? getStoredUser() : null;
    const storeLabel = user?.store_name?.trim() || user?.name?.trim() || '';
    const title = buildPdfTitle(inputs.productName);

    let y = PAGE_MARGIN;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.setTextColor(30, 30, 30);
    const titleLines = doc.splitTextToSize(title, pageWidth - PAGE_MARGIN * 2);
    titleLines.forEach((line, index) => {
        doc.text(line, pageWidth / 2, y + index * (LINE_HEIGHT + 1), { align: 'center' });
    });
    y += titleLines.length * (LINE_HEIGHT + 1) + 2;

    if (storeLabel) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(storeLabel, pageWidth / 2, y, { align: 'center' });
        y += LINE_HEIGHT;
    }

    y += SECTION_GAP;

    y = writeSectionTitle(doc, y, 'Dados informados');
    buildInputRows(inputs).forEach(([label, value]) => {
        y = writeKeyValue(doc, y, label, value);
    });

    y += SECTION_GAP;
    y = writeSectionTitle(doc, y, 'Resultado');
    const quantityLabel = `${quote.quantity || 1} ${(quote.quantity || 1) === 1 ? 'unidade' : 'unidades'}`;
    const productLabel = String(inputs.productName || '').trim();
    y = writeKeyValue(
        doc,
        y,
        'Cálculo',
        productLabel ? `${productLabel} · ${quantityLabel}` : quantityLabel,
    );

    y = writeKeyValue(doc, y, 'Sugestão de venda', formatCurrencyInFloat(quote.suggestedListPrice), {
        bold: true,
        subtitle: [unitSubtitle(quote.suggestedListPrice, quote.quantity), `${formatPercent(quote.marginOnSuggested)} de lucro`]
            .filter(Boolean)
            .join(' · '),
    });
    y = writeKeyValue(doc, y, 'Valor desejado', formatCurrencyInFloat(quote.salePrice), {
        bold: true,
        subtitle: [unitSubtitle(quote.salePrice, quote.quantity), `${formatPercent(quote.marginOnSale)} de lucro`]
            .filter(Boolean)
            .join(' · '),
    });

    if (quote.totalMinutes > 0 || quote.totalGrams > 0) {
        y = writeKeyValue(
            doc,
            y,
            'Tempo total / filamento',
            `${formatMinutes(quote.totalMinutes)} · ${quote.totalGrams.toFixed(0)} g`,
        );
    }

    y += SECTION_GAP;
    y = writeSectionTitle(doc, y, 'Detalhamento — custos da impressão');
    buildProductionRows(quote).forEach((row, index, arr) => {
        const subtitle = [row.note, row.unitCaption].filter(Boolean).join(' · ');
        y = writeKeyValue(doc, y, row.label, formatCurrencyInFloat(row.amount), {
            bold: index === arr.length - 1,
            subtitle: subtitle || undefined,
        });
    });

    y += SECTION_GAP;
    y = writeSectionTitle(doc, y, 'Resumo');
    y = writeKeyValue(doc, y, 'Custo de produção', formatCurrencyInFloat(quote.productionCost));
    y = writeKeyValue(doc, y, 'Preço com markup', formatCurrencyInFloat(quote.priceWithMarkup), {
        subtitle: `${formatPercent(quote.marginOnMarkup)} de lucro`,
    });

    if (quote.salePrice > 0 && quote.totalFeesOnInformedPrice > 0) {
        y = writeKeyValue(
            doc,
            y,
            'Taxas sobre valor desejado',
            formatCurrencyInFloat(quote.totalFeesOnInformedPrice),
        );
        y = writeKeyValue(doc, y, 'Líquido após taxas', formatCurrencyInFloat(quote.netFromSale));
    }

    const totalMinutes =
        inputs.multipartEnabled && Array.isArray(inputs.parts)
            ? inputs.parts.reduce((acc, part) => acc + printTimeToMinutes(part.time), 0)
            : printTimeToMinutes(inputs.printTime);

    if (totalMinutes > 0 && quote.costPerHour > 0) {
        y += SECTION_GAP;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(120, 120, 120);
        y = ensureSpace(doc, y, LINE_HEIGHT * 2);
        doc.text(
            `Depreciação: ${formatCurrencyInFloat(quote.costPerHour)}/h · ${(totalMinutes / 60).toFixed(2).replace('.', ',')} h de impressão`,
            PAGE_MARGIN,
            y,
        );
    }

    addFooters(doc, calculatorUrl);
    downloadPdfBlob(doc, buildPdfFileName(inputs.productName));
}

export { slugifyProductName, buildPdfFileName, buildPdfTitle };
